import React, { useState } from 'react';
import logger from '../utils/logger';

const API_BASE =
  typeof window !== 'undefined'
    ? 'https://www.whydiditdie.com' // use the non-redirecting backend origin
    : 'https://www.whydiditdie.com';

const SubscribePage = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setMessage('');

    const url = `${API_BASE}/api/subscribe`;
    logger.info('[Subscribe] POST ->', url, { email });

    try {
      const res = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        // credentials not needed for this endpoint; leave out to avoid stricter CORS
        body: JSON.stringify({ email }),
        redirect: 'error', // fail fast if any redirect happens (helps catch wrong host)
        referrerPolicy: 'no-referrer',
      });

      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        /* ignore non-JSON */
      }

      if (res.ok) {
        setStatus('success');
        setMessage(
          data.message ||
            "You're in! Get ready for AI marketing intelligence that actually makes sense."
        );
        setEmail('');
      } else {
        setStatus('error');
        setMessage(
          data.error ||
            'Our subscribe button just became a case study. Please try again!'
        );
      }
    } catch (err) {
      console.error('[Subscribe] Error:', err);
      setStatus('error');
      setMessage(
        'Access blocked by browser (CORS/redirect). Please refresh and try again.'
      );
    }
  };

  return (
    <div className="bg-black text-white min-h-screen font-sans">
      <nav className="fixed top-0 left-0 right-0 z-50 py-2 bg-black/95 backdrop-blur-xl border-b border-orange-400/20">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <a href="/" className="flex items-center cursor-pointer hover:scale-105 transition-transform">
              <span className="text-xl sm:text-2xl font-black text-orange-400 tracking-tight">WhyDidItDie</span>
            </a>
            <div className="flex items-center gap-3">
              <a href="https://app.whydiditdie.com/login" className="px-4 py-2 border border-white/20 rounded-lg font-semibold text-sm hover:bg-white/10 hover:border-white/40 transition-all">
                Login
              </a>
              <a href="https://app.whydiditdie.com/signup" className="px-4 py-2 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg font-bold text-sm shadow-lg shadow-orange-500/40 hover:-translate-y-1 transition-all">
                Start Free Trial
              </a>
            </div>
          </div>
        </div>
      </nav>

      <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20 px-5">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-400/15 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-red-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

        <div className="max-w-2xl mx-auto text-center relative z-10">
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6">
              Subscribe Before We Start
              <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent"> Charging for This</span>
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed text-white/80 mb-4">
              Weekly marketing intelligence from our 8 AI modules. Campaign forensics, waste detection,
              global market insights, and psychological copywriting secrets. You know, the stuff your competitors
              are probably already reading.
            </p>
            <p className="text-base text-white/60 italic mb-8">
              (We're a startup. Please don't unsubscribe. Our investor deck literally says "growing email list" as a KPI.)
            </p>
          </div>

          {status === 'success' ? (
            <div className="bg-green-500/20 border border-green-500/30 rounded-2xl p-8 mb-6 animate-fade-in">
              <div className="text-5xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-green-400 mb-3">You're In!</h3>
              <p className="text-white/90 leading-relaxed">{message}</p>
              <p className="text-sm text-white/60 mt-4">Check your email for confirmation (or spam folder, because irony).</p>
            </div>
          ) : (
            <div className="mb-8">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8">
                <form className="flex flex-col sm:flex-row gap-4 mb-4" onSubmit={handleSubmit} noValidate>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@company.com"
                    autoComplete="email"
                    required
                    disabled={status === 'loading'}
                    className="flex-1 px-6 py-4 bg-black/50 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-orange-400/50 focus:ring-2 focus:ring-orange-400/20 transition-all disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading' || !email}
                    className="px-8 py-4 bg-gradient-to-r from-orange-400 to-red-500 rounded-xl font-bold shadow-lg shadow-orange-500/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 whitespace-nowrap"
                  >
                    {status === 'loading' ? (
                      <span className="flex items-center gap-2">
                        <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Subscribing...
                      </span>
                    ) : (
                      'Subscribe (It\'s Free)'
                    )}
                  </button>
                </form>

                {status === 'error' && (
                  <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 text-sm text-red-300">
                    {message}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-left">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">What You'll Get (Besides Our Sparkling Personality)</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <span className="text-2xl flex-shrink-0">🔬</span>
                <div>
                  <h4 className="font-bold text-orange-400 mb-1">Campaign Forensics Reports</h4>
                  <p className="text-sm text-white/70">Real autopsies of campaigns that died spectacularly. Learn from others' mistakes so you don't have to make your own.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl flex-shrink-0">🤖</span>
                <div>
                  <h4 className="font-bold text-orange-400 mb-1">AI Marketing Intelligence</h4>
                  <p className="text-sm text-white/70">The latest in AI-powered marketing insights. Like having a fortune teller, but with actual data.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl flex-shrink-0">💰</span>
                <div>
                  <h4 className="font-bold text-orange-400 mb-1">Waste Detection Tips</h4>
                  <p className="text-sm text-white/70">How to stop hemorrhaging money on ads that don't work. Your CFO will love you.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl flex-shrink-0">🎯</span>
                <div>
                  <h4 className="font-bold text-orange-400 mb-1">Platform Strategy Secrets</h4>
                  <p className="text-sm text-white/70">Which platforms are worth your budget and which ones are just expensive popularity contests.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl flex-shrink-0">😏</span>
                <div>
                  <h4 className="font-bold text-orange-400 mb-1">Sarcastic Commentary</h4>
                  <p className="text-sm text-white/70">Because marketing is serious business, but that doesn't mean we can't roast it occasionally.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2"><span className="text-green-500">✓</span><span>No spam ever</span></div>
            <div className="flex items-center gap-2"><span className="text-green-500">✓</span><span>Unsubscribe anytime</span></div>
            <div className="flex items-center gap-2"><span className="text-green-500">✓</span><span>Your data stays yours</span></div>
          </div>

          <p className="mt-6 text-xs text-white/40">
            By subscribing, you agree to receive emails from WhyDidItDie. We respect your inbox more than most marketers respect their budgets.
          </p>
        </div>
      </section>

      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(20px);} to { opacity: 1; transform: translateY(0);} }
        .animate-fade-in { animation: fade-in 0.6s ease-out; }
      `}</style>
    </div>
  );
};

export default SubscribePage;
